'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';
import { Search, Palette, Wrench, Sparkles, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

const journeySteps = [
    {
        step: 1,
        icon: Search,
        titleKey: 'journey.steps.consultation.title',
        descriptionKey: 'journey.steps.consultation.description',
        durationKey: 'journey.steps.consultation.duration',
        detailsKey: 'journey.steps.consultation.details'
    },
    {
        step: 2,
        icon: Palette,
        titleKey: 'journey.steps.design.title',
        descriptionKey: 'journey.steps.design.description',
        durationKey: 'journey.steps.design.duration',
        detailsKey: 'journey.steps.design.details'
    },
    {
        step: 3,
        icon: Wrench,
        titleKey: 'journey.steps.preparation.title',
        descriptionKey: 'journey.steps.preparation.description',
        durationKey: 'journey.steps.preparation.duration',
        detailsKey: 'journey.steps.preparation.details'
    },
    {
        step: 4,
        icon: Sparkles,
        titleKey: 'journey.steps.reveal.title',
        descriptionKey: 'journey.steps.reveal.description',
        durationKey: 'journey.steps.reveal.duration',
        detailsKey: 'journey.steps.reveal.details'
    }
];

export default function SmileJourneySection() {
    const { t } = useTranslation('hollywood-smile');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const progressLineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

            // Timeline steps animation
            if (timelineRef.current?.children) {
                gsap.fromTo(timelineRef.current.children,
                    { opacity: 0, x: isRTL ? 50 : -50, scale: 0.9 },
                    {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: timelineRef.current,
                            start: "top 80%",
                            end: "bottom 20%",
                            once: true
                        }
                    }
                );
            }

            // Animated progress line
            gsap.fromTo(progressLineRef.current,
                { scaleY: 0 },
                {
                    scaleY: 1,
                    duration: 2,
                    ease: "power2.out",
                    transformOrigin: "top",
                    scrollTrigger: {
                        trigger: timelineRef.current,
                        start: "top 70%",
                        end: "bottom 30%",
                        once: true
                    }
                }
            );

        }, sectionRef);

        return () => ctx.revert();
    }, [isRTL]);

    return (
        <section
            id="journey"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                    >
                        {t('journey.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('journey.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>
                </div>

                {/* Timeline */}
                <div className="relative max-w-4xl mx-auto">
                    {/* Progress Line */}
                    <div className="absolute start-8 md:start-12 top-0 bottom-0 w-1 bg-slate-200 dark:bg-slate-700 rounded-full">
                        <div
                            ref={progressLineRef}
                            className="w-full h-full bg-gold rounded-full origin-top"
                        ></div>
                    </div>

                    {/* Timeline Steps */}
                    <div ref={timelineRef} className="space-y-12">
                        {journeySteps.map((step, index) => (
                            <motion.div
                                key={index}
                                className="relative flex items-start gap-8"
                                whileHover={{ x: isRTL ? -10 : 10 }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Step Number Circle */}
                                <div className="relative z-10 w-16 h-16 md:w-24 md:h-24 bg-gold rounded-full flex items-center justify-center shadow-xl">
                                    <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                                        <step.icon className="w-6 h-6 md:w-8 md:h-8 text-gold" />
                                    </div>

                                    {/* Step number badge */}
                                    <div className="absolute -top-2 -end-2 w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm font-bold">
                                        {step.step}
                                    </div>
                                </div>

                                {/* Content Card */}
                                <div className="flex-1 relative bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200 dark:border-slate-700 group hover:shadow-2xl transition-all duration-300">
                                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                                        <div className="flex-1">
                                            <h3 className="text-2xl md:text-3xl font-bold text-navy dark:text-white mb-3 group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300">
                                                {t(step.titleKey)}
                                            </h3>
                                            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
                                                {t(step.descriptionKey)}
                                            </p>
                                        </div>

                                        {/* Duration Badge — self-start + inline-flex keeps
                                            the pill natural-width on mobile so it doesn't
                                            stretch to fill the column. */}
                                        <div className={cn(
                                            "inline-flex items-center gap-2 bg-gold/10 text-gold px-3 py-1.5 rounded-full border border-gold/20 shrink-0 self-start",
                                            isRTL && "flex-row-reverse"
                                        )}>
                                            <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
                                            <span className="text-sm font-medium leading-none">{t(step.durationKey)}</span>
                                        </div>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {(() => {
                                            const details = t(step.detailsKey, { returnObjects: true });
                                            if (Array.isArray(details)) {
                                                return details.map((detail: string, detailIndex: number) => (
                                                    <div
                                                        key={detailIndex}
                                                        className={cn(
                                                            "flex items-center text-slate-600 dark:text-slate-300",
                                                            isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                                                        )}
                                                    >
                                                        <div className="w-2 h-2 bg-gold rounded-full shrink-0"></div>
                                                        <span>{detail}</span>
                                                    </div>
                                                ));
                                            }
                                            return null;
                                        })()}
                                    </div>

                                    {/* Hover effect border */}
                                    <div className="absolute inset-0 rounded-2xl border-2 border-gold opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Duration Note */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <div className="bg-gold/10 rounded-2xl p-8 border border-gold/20 max-w-3xl mx-auto">
                        <div className={cn(
                            "flex items-center justify-center mb-4",
                            isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                        )}>
                            <Clock className="w-8 h-8 text-gold" />
                            <h3 className="text-2xl font-bold text-navy dark:text-white">
                                {t('journey.timeline.title')}
                            </h3>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300 text-lg">
                            {t('journey.timeline.description')}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
